import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ArrowLeft, ChevronLeft, ChevronRight, Loader2, AlertTriangle, Car, FileText, ExternalLink, Clock } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { VehicleForm } from './VehicleForm';
import { VehicleFormValues } from './schema';
import { getVehicle, createVehicle, updateVehicle } from '@/app/services/vehicleService';
import { listJobCards } from '@/app/services/jobCardService';
import { VehicleCreate, VehicleUpdate } from '@/app/types/vehicle';
import { format } from 'date-fns';
import { StatusBadge, PriorityBadge } from '../../components/PriorityBadge';

const VehicleDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const isEditMode = !!id;
  const [historyPage, setHistoryPage] = useState(0);
  const HISTORY_PAGE_SIZE = 10;

  // Fetch vehicle data if editing
  const {
    data: vehicle,
    isLoading: isLoadingVehicle,
    isError: isVehicleError,
    error: vehicleError,
  } = useQuery({
    queryKey: ['vehicle', id],
    queryFn: () => getVehicle(Number(id)),
    enabled: isEditMode,
  });

  // Fetch service history (job cards for this vehicle)
  const { data: jobCardsData, isLoading: isLoadingJobCards } = useQuery({
    queryKey: ['vehicle-job-cards', id, historyPage],
    queryFn: () =>
      listJobCards({
        vehicle_id: Number(id),
        skip: historyPage * HISTORY_PAGE_SIZE,
        limit: HISTORY_PAGE_SIZE,
        sort_by: 'transaction_date',
        sort_dir: 'desc',
      }),
    enabled: isEditMode,
  });

  const historyPageCount = jobCardsData
    ? Math.ceil(jobCardsData.total / HISTORY_PAGE_SIZE)
    : 0;

  // Compute days since last service from the most recent job card
  const daysSinceLastService = useMemo(() => {
    if (!jobCardsData?.items?.length && historyPage > 0) return null;
    // On page 0, the first item is the most recent (sorted desc by transaction_date)
    if (historyPage !== 0 || !jobCardsData?.items?.length) return null;
    const mostRecentDate = new Date(jobCardsData.items[0].transaction_date);
    const diffMs = Date.now() - mostRecentDate.getTime();
    return Math.floor(diffMs / 86400000);
  }, [jobCardsData, historyPage]);

  // Create vehicle mutation
  const createMutation = useMutation({
    mutationFn: (data: VehicleCreate) => createVehicle(data),
    onSuccess: (newVehicle) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      toast.success(`Vehicle ${newVehicle.registration_number} registered successfully`);
      navigate('/workshop/vehicles/list');
    },
    onError: (error: Error) => {
      toast.error(`Failed to register vehicle: ${error.message}`);
    },
  });

  // Update vehicle mutation
  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: VehicleUpdate }) => updateVehicle(id, data),
    onSuccess: (updatedVehicle) => {
      queryClient.invalidateQueries({ queryKey: ['vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['vehicle', id] });
      toast.success(`Vehicle ${updatedVehicle.registration_number} updated successfully`);
    },
    onError: (error: Error) => {
      toast.error(`Failed to update vehicle: ${error.message}`);
    },
  });

  const handleSubmit = (data: VehicleFormValues): void => {
    // Transform form data to API format
    const apiData: VehicleCreate | VehicleUpdate = {
      registration_number: data.registration_number,
      make: data.make || null,
      model: data.model || null,
      variant: data.variant || null,
      year_of_manufacture: data.year_of_manufacture || null,
      color: data.color || null,
      vin_chassis: data.vin_chassis || null,
      engine_number: data.engine_number || null,
      fuel_type: data.fuel_type || null,
      transmission: data.transmission || null,
      owner_id: data.owner_id || null,
      insurance_provider: data.insurance_provider || null,
      insurance_policy_number: data.insurance_policy_number || null,
      insurance_expiry: data.insurance_expiry || null,
      site_id: data.site_id || null,
    };

    if (isEditMode && id) {
      updateMutation.mutate({ id: Number(id), data: apiData });
    } else {
      createMutation.mutate(apiData as VehicleCreate);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;

  // Loading state for edit mode
  if (isEditMode && isLoadingVehicle) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center gap-4">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-[400px] w-full" />
      </div>
    );
  }

  // Error state
  if (isEditMode && isVehicleError) {
    return (
      <div className="container mx-auto py-6 space-y-6">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertDescription>
            Error loading vehicle: {(vehicleError as Error).message}
          </AlertDescription>
        </Alert>
        <Button variant="outline" onClick={() => navigate('/workshop/vehicles/list')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Vehicles
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/workshop/vehicles/list')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            <Car className="h-6 w-6" />
            <h1 className="text-2xl font-bold">
              {isEditMode ? `Edit Vehicle: ${vehicle?.registration_number}` : 'Register New Vehicle'}
            </h1>
          </div>
        </div>
        {isEditMode && (
          <Button asChild>
            <Link to={`/workshop/job-cards/detail?vehicle_id=${id}`}>
              <FileText className="h-4 w-4 mr-2" />
              Create Job Card
            </Link>
          </Button>
        )}
      </div>

      {/* Vehicle Form */}
      <VehicleForm
        vehicle={vehicle}
        onSubmit={handleSubmit}
        isLoading={isLoading}
        isEditMode={isEditMode}
      />

      {/* Service History (only in edit mode) */}
      {isEditMode && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Service History
                {daysSinceLastService !== null && (
                  <span className="ml-2 text-sm font-normal text-muted-foreground flex items-center gap-1">
                    <Clock className="h-3.5 w-3.5" />
                    {daysSinceLastService === 0
                      ? 'Last service today'
                      : daysSinceLastService === 1
                        ? '1 day since last service'
                        : `${daysSinceLastService} days since last service`}
                  </span>
                )}
              </CardTitle>
              {jobCardsData && jobCardsData.total > 0 && vehicle?.registration_number && (
                <Button variant="outline" size="sm" asChild>
                  <Link
                    to={`/workshop/job-cards/list?filters=${encodeURIComponent(
                      JSON.stringify({ 'vehicle.registration_number': vehicle.registration_number })
                    )}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5 mr-1.5" />
                    View all job cards
                  </Link>
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isLoadingJobCards ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin" />
              </div>
            ) : jobCardsData?.items && jobCardsData.items.length > 0 ? (
              <div className="space-y-2">
                {jobCardsData.items.map((jobCard) => (
                  <div
                    key={jobCard.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted cursor-pointer"
                    onClick={() => navigate(`/workshop/job-cards/detail/${jobCard.id}`)}
                  >
                    <div className="flex items-center gap-4">
                      <div>
                        <div className="font-medium">{jobCard.document_number}</div>
                        <div className="text-sm text-muted-foreground">
                          {format(new Date(jobCard.transaction_date), 'dd/MM/yyyy')}
                        </div>
                      </div>
                      {jobCard.customer_complaint && (
                        <div className="text-sm text-muted-foreground max-w-md truncate">
                          {jobCard.customer_complaint}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <PriorityBadge priority={jobCard.priority} />
                      <StatusBadge status={jobCard.status} />
                    </div>
                  </div>
                ))}

                {/* Pagination controls */}
                {historyPageCount > 1 && (
                  <div className="flex items-center justify-between pt-2">
                    <div className="text-xs sm:text-sm text-muted-foreground">
                      Page {historyPage + 1} of {historyPageCount}
                      {jobCardsData.total > 0 && (
                        <span className="ml-1">({jobCardsData.total} total)</span>
                      )}
                    </div>
                    <div className="flex items-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHistoryPage((p) => Math.max(0, p - 1))}
                        disabled={historyPage === 0 || isLoadingJobCards}
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline ml-1">Previous</span>
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setHistoryPage((p) => p + 1)}
                        disabled={historyPage + 1 >= historyPageCount || isLoadingJobCards}
                      >
                        <span className="hidden sm:inline mr-1">Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center text-muted-foreground py-8">
                No service history found for this vehicle.
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VehicleDetailPage;
